import * as THREE from 'three';
import { BinaryHeap } from '../shared/binary-heap.js';
import { ClipTask, ClipMethod } from '../shared/defines.js';
import { Box3Helper } from '../viewer-utils/tools/box3-helper.js';
import { getLru, getPointLoadLimit } from '../services/config.service';

// Pre-allocated scratch objects for the hot visibility loop to avoid GC pressure
const _scratch = {
  px: new THREE.Vector3(),
  nx: new THREE.Vector3(),
  py: new THREE.Vector3(),
  ny: new THREE.Vector3(),
  pz: new THREE.Vector3(),
  nz: new THREE.Vector3(),
  pxN: new THREE.Vector3(),
  nxN: new THREE.Vector3(),
  pyN: new THREE.Vector3(),
  nyN: new THREE.Vector3(),
  pzN: new THREE.Vector3(),
  nzN: new THREE.Vector3(),
  pxPlane: new THREE.Plane(),
  nxPlane: new THREE.Plane(),
  pyPlane: new THREE.Plane(),
  nyPlane: new THREE.Plane(),
  pzPlane: new THREE.Plane(),
  nzPlane: new THREE.Plane(),
  frustum: new THREE.Frustum(),
  pcWorldInverse: new THREE.Matrix4(),
  toPCObject: new THREE.Matrix4(),
};

export function updatePointClouds(
  pointclouds: any[],
  camera: any,
  renderer: any,
  config: any,
): any {
  for (let pointcloud of pointclouds) {
    let start = performance.now();

    for (let profileRequest of pointcloud.profileRequests) {
      profileRequest.update();

      let duration = performance.now() - start;
      if (duration > 5) {
        break;
      }
    }
  }

  let result = updateVisibility(pointclouds, camera, renderer, config);
  for (let pointcloud of pointclouds) {
    pointcloud.updateMaterial(pointcloud.material, pointcloud.visibleNodes, camera, renderer);
    pointcloud.updateVisibleBounds();
  }

  getLru().freeMemory(getPointLoadLimit());

  return result;
}

export function updateVisibilityStructures(pointclouds: any[], camera: any, renderer: any): any {
  let frustums: THREE.Frustum[] = [];
  let camObjPositions: THREE.Vector3[] = [];
  let priorityQueue = new BinaryHeap(function (x: any) {
    return 1 / x.weight;
  });

  for (let i = 0; i < pointclouds.length; i++) {
    let pointcloud = pointclouds[i];

    if (!pointcloud.initialized()) {
      continue;
    }

    pointcloud.numVisibleNodes = 0;
    pointcloud.numVisiblePoints = 0;
    pointcloud.deepestVisibleLevel = 0;
    pointcloud.visibleNodes = [];
    pointcloud.visibleGeometry = [];

    // frustum in object space
    camera.updateMatrixWorld();
    let frustum = new THREE.Frustum();
    let viewI = camera.matrixWorldInverse;
    let world = pointcloud.matrixWorld;

    // use close near plane for frustum intersection
    let frustumCam = camera.clone();
    frustumCam.near = Math.min(camera.near, 0.1);
    frustumCam.updateProjectionMatrix();
    let proj = camera.projectionMatrix;

    let fm = new THREE.Matrix4().multiply(proj).multiply(viewI).multiply(world);
    frustum.setFromProjectionMatrix(fm);
    frustums.push(frustum);

    // camera position in object space
    let view = camera.matrixWorld;
    let worldI = world.clone().invert();
    let camMatrixObject = new THREE.Matrix4().multiply(worldI).multiply(view);
    let camObjPos = new THREE.Vector3().setFromMatrixPosition(camMatrixObject);
    camObjPositions.push(camObjPos);

    if (pointcloud.visible && pointcloud.root !== null) {
      priorityQueue.push({ pointcloud: i, node: pointcloud.root, weight: Number.MAX_VALUE });
    }

    if (pointcloud.root.isTreeNode()) {
      pointcloud.hideDescendants(pointcloud.root.sceneNode);
    }

    for (let j = 0; j < pointcloud.boundingBoxNodes.length; j++) {
      pointcloud.boundingBoxNodes[j].visible = false;
    }
  }

  return {
    frustums: frustums,
    camObjPositions: camObjPositions,
    priorityQueue: priorityQueue,
  };
}

export function updateVisibility(pointclouds: any[], camera: any, renderer: any, config: any): any {
  // Configuration is now required - no fallback to globals
  const pointBudget = config.pointBudget;
  const maxNodesLoading = config.maxNodesLoading;
  const transformVersions = config.transformVersions;
  let numVisibleNodes = 0;
  let numVisiblePoints = 0;

  let numVisiblePointsInPointclouds = new Map(pointclouds.map(pc => [pc, 0]));

  let visibleNodes: any[] = [];
  let visibleGeometry: any[] = [];
  let unloadedGeometry: any[] = [];

  let lowestSpacing = Infinity;

  // calculate object space frustum and cam pos and setup priority queue
  let s = updateVisibilityStructures(pointclouds, camera, renderer);
  let frustums = s.frustums;
  let camObjPositions = s.camObjPositions;
  let priorityQueue = s.priorityQueue;

  let loadedToGPUThisFrame = 0;

  let domWidth = renderer.domElement.clientWidth;
  let domHeight = renderer.domElement.clientHeight;

  // check if pointcloud has been transformed
  // some code will only be executed if changes have been detected
  let pointcloudTransformVersion = transformVersions || new Map();

  for (let pointcloud of pointclouds) {
    if (!pointcloud.visible) {
      continue;
    }

    pointcloud.updateMatrixWorld();

    if (!pointcloudTransformVersion.has(pointcloud)) {
      pointcloudTransformVersion.set(pointcloud, {
        number: 0,
        transform: pointcloud.matrixWorld.clone(),
      });
    } else {
      let version = pointcloudTransformVersion.get(pointcloud);

      if (!version.transform.equals(pointcloud.matrixWorld)) {
        version.number++;
        version.transform.copy(pointcloud.matrixWorld);

        pointcloud.dispatchEvent({
          type: 'transformation_changed',
          target: pointcloud,
        });
      }
    }
  }

  while (priorityQueue.size() > 0) {
    let element = priorityQueue.pop();
    let node = element.node;
    let parent = element.parent;
    let pointcloud = pointclouds[element.pointcloud];

    let box = node.getBoundingBox();
    let frustum = frustums[element.pointcloud];
    let camObjPos = camObjPositions[element.pointcloud];

    let insideFrustum = frustum.intersectsBox(box);
    let maxLevel = pointcloud.maxLevel || Infinity;
    let level = node.getLevel();
    let visible = insideFrustum;
    visible = visible && !(numVisiblePoints + node.getNumPoints() > pointBudget);
    visible =
      visible &&
      !(
        numVisiblePointsInPointclouds.get(pointcloud)! + node.getNumPoints() >
        pointcloud.pointBudget
      );
    visible = visible && level < maxLevel;
    visible = visible || node.getLevel() <= 2;

    let clipBoxes = pointcloud.material.clipBoxes;
    if (true && clipBoxes.length > 0) {
      let numIntersecting = 0;
      let numIntersectionVolumes = 0;

      for (let clipBox of clipBoxes) {
        _scratch.pcWorldInverse.copy(pointcloud.matrixWorld).invert();
        _scratch.toPCObject.copy(_scratch.pcWorldInverse).multiply(clipBox.box.matrixWorld);

        _scratch.px.set(+0.5, 0, 0).applyMatrix4(_scratch.toPCObject);
        _scratch.nx.set(-0.5, 0, 0).applyMatrix4(_scratch.toPCObject);
        _scratch.py.set(0, +0.5, 0).applyMatrix4(_scratch.toPCObject);
        _scratch.ny.set(0, -0.5, 0).applyMatrix4(_scratch.toPCObject);
        _scratch.pz.set(0, 0, +0.5).applyMatrix4(_scratch.toPCObject);
        _scratch.nz.set(0, 0, -0.5).applyMatrix4(_scratch.toPCObject);

        _scratch.pxN.subVectors(_scratch.nx, _scratch.px).normalize();
        _scratch.nxN.copy(_scratch.pxN).multiplyScalar(-1);
        _scratch.pyN.subVectors(_scratch.ny, _scratch.py).normalize();
        _scratch.nyN.copy(_scratch.pyN).multiplyScalar(-1);
        _scratch.pzN.subVectors(_scratch.nz, _scratch.pz).normalize();
        _scratch.nzN.copy(_scratch.pzN).multiplyScalar(-1);

        _scratch.pxPlane.setFromNormalAndCoplanarPoint(_scratch.pxN, _scratch.px);
        _scratch.nxPlane.setFromNormalAndCoplanarPoint(_scratch.nxN, _scratch.nx);
        _scratch.pyPlane.setFromNormalAndCoplanarPoint(_scratch.pyN, _scratch.py);
        _scratch.nyPlane.setFromNormalAndCoplanarPoint(_scratch.nyN, _scratch.ny);
        _scratch.pzPlane.setFromNormalAndCoplanarPoint(_scratch.pzN, _scratch.pz);
        _scratch.nzPlane.setFromNormalAndCoplanarPoint(_scratch.nzN, _scratch.nz);

        _scratch.frustum.set(
          _scratch.pxPlane,
          _scratch.nxPlane,
          _scratch.pyPlane,
          _scratch.nyPlane,
          _scratch.pzPlane,
          _scratch.nzPlane,
        );
        let intersects = _scratch.frustum.intersectsBox(box);

        if (intersects) {
          numIntersecting++;
        }
        numIntersectionVolumes++;
      }

      let insideAny = numIntersecting > 0;
      let insideAll = numIntersecting === numIntersectionVolumes;

      if (pointcloud.material.clipTask === ClipTask.SHOW_INSIDE) {
        if (pointcloud.material.clipMethod === ClipMethod.INSIDE_ANY && insideAny) {
          // visible remains true
        } else if (pointcloud.material.clipMethod === ClipMethod.INSIDE_ALL && insideAll) {
          // visible remains true
        } else {
          visible = false;
        }
      }
    }

    if (node.spacing) {
      lowestSpacing = Math.min(lowestSpacing, node.spacing);
    } else if (node.geometryNode && node.geometryNode.spacing) {
      lowestSpacing = Math.min(lowestSpacing, node.geometryNode.spacing);
    }

    if (numVisiblePoints + node.getNumPoints() > pointBudget) {
      break;
    }

    if (!visible) {
      continue;
    }

    numVisiblePoints += node.getNumPoints();
    let numVisiblePointsInPointcloud = numVisiblePointsInPointclouds.get(pointcloud)!;
    numVisiblePointsInPointclouds.set(
      pointcloud,
      numVisiblePointsInPointcloud + node.getNumPoints(),
    );

    pointcloud.numVisibleNodes++;
    pointcloud.numVisiblePoints += node.getNumPoints();

    if (node.isGeometryNode() && (!parent || parent.isTreeNode())) {
      if (node.isLoaded() && loadedToGPUThisFrame < 2) {
        node = pointcloud.toTreeNode(node, parent);
        loadedToGPUThisFrame++;
      } else {
        unloadedGeometry.push(node);
        visibleGeometry.push(node);
      }
    }

    if (node.isTreeNode()) {
      getLru().touch(node.geometryNode);
      node.sceneNode.visible = true;
      node.sceneNode.material = pointcloud.material;

      visibleNodes.push(node);
      pointcloud.visibleNodes.push(node);

      if (node._transformVersion === undefined) {
        node._transformVersion = -1;
      }
      let transformVersion = pointcloudTransformVersion.get(pointcloud);
      if (node._transformVersion !== transformVersion.number) {
        node.sceneNode.updateMatrix();
        node.sceneNode.matrixWorld.multiplyMatrices(pointcloud.matrixWorld, node.sceneNode.matrix);
        node._transformVersion = transformVersion.number;
      }

      if (pointcloud.showBoundingBox && !node.boundingBoxNode && node.getBoundingBox) {
        let boxHelper = new Box3Helper(node.getBoundingBox());
        boxHelper.matrixAutoUpdate = false;
        pointcloud.boundingBoxNodes.push(boxHelper);
        node.boundingBoxNode = boxHelper;
        node.boundingBoxNode.matrix.copy(pointcloud.matrixWorld);
      } else if (pointcloud.showBoundingBox) {
        node.boundingBoxNode.visible = true;
        node.boundingBoxNode.matrix.copy(pointcloud.matrixWorld);
      } else if (!pointcloud.showBoundingBox && node.boundingBoxNode) {
        node.boundingBoxNode.visible = false;
      }
    }

    // add child nodes to priorityQueue
    let children = node.getChildren();
    for (let i = 0; i < children.length; i++) {
      let child = children[i];

      let weight = 0;
      if (camera.isPerspectiveCamera) {
        let sphere = child.getBoundingSphere();
        let center = sphere.center;

        let dx = camObjPos.x - center.x;
        let dy = camObjPos.y - center.y;
        let dz = camObjPos.z - center.z;

        let dd = dx * dx + dy * dy + dz * dz;
        let distance = Math.sqrt(dd);

        let radius = sphere.radius;

        let fov = (camera.fov * Math.PI) / 180;
        let slope = Math.tan(fov / 2);
        let projFactor = (0.5 * domHeight) / (slope * distance);
        let screenPixelRadius = radius * projFactor;

        if (screenPixelRadius < pointcloud.minimumNodePixelSize) {
          continue;
        }

        weight = screenPixelRadius;

        if (distance - radius < 0) {
          weight = Number.MAX_VALUE;
        }
      } else {
        // TODO ortho visibility
        let bb = child.getBoundingBox();
        let distance = child.getBoundingSphere().center.distanceTo(camObjPos);
        let diagonal = bb.max.clone().sub(bb.min).length();

        weight = diagonal;
      }

      priorityQueue.push({
        pointcloud: element.pointcloud,
        node: child,
        parent: node,
        weight: weight,
      });
    }
  } // end priority queue loop

  {
    // update DEM
    let maxDEMLevel = 4;
    let candidates = pointclouds.filter(
      (p: any) => p.generateDEM && p.dem && typeof p.dem.update === 'function',
    );
    for (let pointcloud of candidates) {
      let updatingNodes = pointcloud.visibleNodes.filter((n: any) => n.getLevel() <= maxDEMLevel);
      pointcloud.dem.update(updatingNodes);
    }
  }

  for (let i = 0; i < Math.min(maxNodesLoading, unloadedGeometry.length); i++) {
    unloadedGeometry[i].load();
  }

  return {
    visibleNodes: visibleNodes,
    numVisiblePoints: numVisiblePoints,
    lowestSpacing: lowestSpacing,
  };
}
